import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { submitApplication } from "@/lib/api";

const SITUATIONS = [
  "Startup with low budget",
  "On benefits or just starting out",
  "Small local business",
  "Sole trader / self-employed",
  "Other",
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  business_name: "",
  business_type: "",
  location: "",
  situation: "",
  description: "",
  website_goals: "",
  budget_status: "",
  consent: true,
};

export default function Apply() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.business_name || !form.business_type || !form.situation || !form.description) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitApplication(form);
      setSuccess(true);
      toast.success("Application submitted — we'll be in touch within 7 days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar showAnnouncement={false} />
        <section className="pt-32 pb-24 max-w-2xl mx-auto px-6 lg:px-10 text-center">
          <CheckCircle2 className="mx-auto text-blue-600" size={56} strokeWidth={1.5} />
          <h1 className="mt-8 font-display text-4xl md:text-5xl font-medium tracking-tighter text-slate-900 text-balance">
            Application received.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Thanks {form.full_name.split(" ")[0]}. We&apos;ll review your application and reply to <strong className="text-slate-900">{form.email}</strong> within 7 days.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/")} className="btn-outline-light" data-testid="apply-back-home">
              Back to home
            </button>
            <Link to="/donate" className="btn-primary" data-testid="apply-donate-link">
              Help fund the next build <ArrowRight size={18} />
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar showAnnouncement={false} />

      <section className="pt-28 pb-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-[14px] mb-8" data-testid="apply-back-link">
            <ArrowLeft size={16} /> Back
          </Link>
          <p className="eyebrow text-blue-600">Apply</p>
          <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02] text-slate-900 text-balance">
            Apply for a free <span className="font-serif-accent text-blue-600">website.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Tell us about your business. Be honest — the strongest applications are clear, specific, and human.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <form
          onSubmit={onSubmit}
          data-testid="apply-form"
          className="max-w-3xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Field label="Full name *" testid="field-fullname">
            <input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="input" required data-testid="apply-input-fullname" />
          </Field>
          <Field label="Email *" testid="field-email">
            <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input" required data-testid="apply-input-email" />
          </Field>
          <Field label="Phone" testid="field-phone">
            <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input" data-testid="apply-input-phone" />
          </Field>
          <Field label="Location (city, UK)" testid="field-location">
            <input value={form.location} onChange={(e) => update("location", e.target.value)} className="input" data-testid="apply-input-location" />
          </Field>
          <Field label="Business name *" testid="field-business">
            <input value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className="input" required data-testid="apply-input-business" />
          </Field>
          <Field label="Business type *" testid="field-businesstype">
            <input placeholder="e.g. mobile mechanic, baker, designer" value={form.business_type} onChange={(e) => update("business_type", e.target.value)} className="input" required data-testid="apply-input-businesstype" />
          </Field>

          <Field label="Your situation *" full testid="field-situation">
            <select value={form.situation} onChange={(e) => update("situation", e.target.value)} className="input" required data-testid="apply-select-situation">
              <option value="">Select an option</option>
              {SITUATIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>

          <Field label="Tell us about your business *" full testid="field-description">
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="input resize-none"
              placeholder="What do you do? Who are your customers? Why do you need a website?"
              required
              data-testid="apply-input-description"
            />
          </Field>

          <Field label="What would success look like?" full testid="field-goals">
            <textarea
              rows={3}
              value={form.website_goals}
              onChange={(e) => update("website_goals", e.target.value)}
              className="input resize-none"
              placeholder="e.g. taking online bookings, showing portfolio, selling 5 items per week"
              data-testid="apply-input-goals"
            />
          </Field>

          <Field label="Budget status" full testid="field-budget">
            <textarea
              rows={2}
              value={form.budget_status}
              onChange={(e) => update("budget_status", e.target.value)}
              className="input resize-none"
              placeholder="Briefly — why a paid build isn't an option for you right now."
              data-testid="apply-input-budget"
            />
          </Field>

          <div className="md:col-span-2 flex items-start gap-3 mt-2">
            <input
              type="checkbox"
              id="consent"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              className="mt-1 accent-blue-600"
              data-testid="apply-consent"
            />
            <label htmlFor="consent" className="text-[14px] text-slate-600 leading-relaxed">
              I consent to DH Website Services storing this information to review my application. We never share data with third parties.
            </label>
          </div>

          <div className="md:col-span-2 mt-6">
            <button
              type="submit"
              disabled={loading || !form.consent}
              data-testid="apply-submit-btn"
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Submitting..." : "Submit application"} {!loading && <ArrowRight size={18} />}
            </button>
            <p className="mt-4 text-center text-[13px] text-slate-500">We review every application personally. You&apos;ll hear back within 7 days.</p>
          </div>
        </form>
      </section>

      <Footer />

      <style>{`
        .input {
          width: 100%;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 15px;
          color: #0f172a;
          font-family: 'Manrope', sans-serif;
          transition: border-color 0.2s, background 0.2s;
        }
        .input:focus {
          outline: none;
          border-color: #2563eb;
          background: #ffffff;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="block text-[13px] font-medium text-slate-700 mb-2">{label}</label>
      {children}
    </div>
  );
}
